var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { AnnotationResultsMessage, Response, SyncMessage, CacheDumpRequestMessage, AnnotationResultStateMessage, AnnotationConfigUpdateMessage } from '@augloop/session-protocol';
import { WorkflowExecutionRequest, WorkflowExecutionResponse, WorkflowRegistrationMessage } from '@augloop/session-protocol-internal';
import { SchemaObject, UpdateAnnotationMetaDataOperation, SignalOperation, AddOperation, Operation, UpdateOperation, DeleteOperation } from '@augloop/types-core';
import { AuthTokenType as ClientAuthTokenType } from '@augloop/schemas';
import { AnnotationActivationTracker } from './annotation-activation-tracker';
import { BatchManager } from './batch-manager';
import { estimateSize } from './estimate-size';
import { EmptySessionManager } from './websocket/empty-session-manager';
import { AuthTokenType, WorkflowKind } from '@augloop/interfaces';
import { isServerOp } from './utils';
import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { getContextTypes } from '@augloop/workflow-requested-contexts';
import { makeItemPathKey, shallowCloneItemWithPath } from '@augloop/session-cache';
import { DynamicWorkflowDefinitionManager, ContextIdManager, WorkflowItemStorage } from '@augloop/mastermind-shared';
var getClientAuthTokenType = function (tokenType) {
    // Right now only have a scenario that involves Substrate, we might have more in the future
    if (tokenType === AuthTokenType.Substrate) {
        return ClientAuthTokenType.Substrate;
    }
    return undefined;
};
var SessionProxy = /** @class */ (function () {
    function SessionProxy(config) {
        this.annotationActivationTrackers = new Map();
        this.annotationCallbacks = new Map();
        this.annotationResultStates = new Map();
        this.tokensByAnnotationType = new Map();
        this.registeredContextTypes = new Set();
        // TODO #5326116: This map acts as a session cache needed for contexts. Remove it in favor of regular session cache once it's available.
        this.availableContexts = new Map();
        this.nextSyncSequenceId = 1;
        this.allowSeed = true;
        this.allowGroupSeed = true;
        this.seedGroupSize = 0;
        this.hasSessionConnected = false;
        this.isSessionClosed = false;
        this.isSessionConnected = false;
        this.workflowDefinitionManager = new DynamicWorkflowDefinitionManager();
        this.contextIdManager = new ContextIdManager();
        this.workflowItemStorage = new WorkflowItemStorage(this.workflowDefinitionManager);
        this.hostCallbacks = config.hostCallbacks;
        this.sessionManager = config.sessionManager;
        this.operationBatchConfig = config.batchOptions ? this.getOperationBatchConfig(config.batchOptions) : undefined;
        this.extensionConfigs = config.extensionConfigs;
        this.clientMetadata = config.clientMetadata;
        this.localWorkflowManager = config.localWorkflowManager;
        this.annotationResultsProcessor = config.annotationResultsProcessor;
        this.localWorkflowOrchestrationMode = config.localWorkflowOrchestrationMode || 0 /* Internal */;
        this.sessionManager.on('sessionClose', this.onSessionClose.bind(this));
        this.sessionManager.on('reconnect', this.onReconnect.bind(this));
        this.sessionManager.on('connect', this.onConnect.bind(this));
        this.sessionManager.on('disconnect', this.onDisconnect.bind(this));
        this.sessionManager.on('resetNextSyncSequenceId', this.resetNextSyncSequenceId.bind(this));
        this.sessionManager.onMessage(AnnotationResultsMessage.getTypeName(), this.onAnnotationResultsFromServer.bind(this));
        this.sessionManager.onMessage(AnnotationResultStateMessage.getTypeName(), this.onAnnotationResultStateMessage.bind(this));
        if (this.localWorkflowOrchestrationMode === 1 /* External */) {
            this.sessionManager.onMessage(WorkflowExecutionRequest.getTypeName(), this.onWorkflowExecutionRequest.bind(this));
        }
    }
    SessionProxy.prototype.initialize = function () {
        var _this = this;
        return this.sessionManager.init(this.extensionConfigs, this.hostCallbacks.onInitSession ? this.hostCallbacks.onInitSession : undefined)
            .then(function () {
            _this.localWorkflowManager.addSession(_this);
            _this.localWorkflowManager.setTokenCallback(_this.getAuthToken.bind(_this));
            return _this;
        });
    };
    Object.defineProperty(SessionProxy.prototype, "isConnected", {
        get: function () {
            return this.isSessionConnected;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionProxy.prototype, "hasConnected", {
        get: function () {
            return this.hasSessionConnected;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionProxy.prototype, "isClosed", {
        get: function () {
            return this.isSessionClosed;
        },
        enumerable: false,
        configurable: true
    });
    SessionProxy.prototype.getContextIdManager = function () {
        return this.contextIdManager;
    };
    SessionProxy.prototype.getWorkflowItemStorage = function () {
        return this.workflowItemStorage;
    };
    SessionProxy.prototype.getWorkflowDefinition = function (workflow, contextId) {
        return this.workflowDefinitionManager.getWorkflowDefinition(workflow, contextId);
    };
    SessionProxy.prototype.registerLocalWorkflow = function (workflow) {
        var e_1, _a;
        if (workflow.kind === WorkflowKind.Join) {
            this.contextIdManager.setActiveScopeType(workflow.collectionScopeType);
            try {
                for (var _b = __values(this.localWorkflowManager.getWorkflowDefinitionsWithSessionAffinity()), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var sessionWorkflow = _c.value;
                    this.contextIdManager.setActiveScopeType(sessionWorkflow.collectionScopeType);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        this.localWorkflowManager.registerLocalWorkflow(workflow, this);
        if (this.localWorkflowOrchestrationMode === 1 /* External */) {
            this.sessionManager.sendMessage(new WorkflowRegistrationMessage({
                workflowDefinition: workflow
            }));
        }
    };
    SessionProxy.prototype.activateAnnotation = function (annotationType, options) {
        var _a;
        var tracker = new AnnotationActivationTracker(this.getSessionManager.bind(this), annotationType, options);
        if (options && options.callback) {
            var map = this.annotationCallbacks.get(annotationType);
            if (!map) {
                map = new Map();
                this.annotationCallbacks.set(annotationType, map);
            }
            map.set(tracker.token, options.callback);
        }
        var tokens = (_a = this.tokensByAnnotationType.get(annotationType)) !== null && _a !== void 0 ? _a : [];
        if (tokens.indexOf(tracker.token) === -1) {
            tokens.push(tracker.token);
        }
        this.tokensByAnnotationType.set(annotationType, tokens);
        this.annotationActivationTrackers.set(tracker.token, tracker);
        tracker.activate(false /*ignoreExistingAnnotations*/);
        return tracker.asPromise();
    };
    SessionProxy.prototype.updateAnnotationConfig = function (token, config) {
        var msg = new AnnotationConfigUpdateMessage({ token: token, config: config });
        this.getSessionManager().sendMessage(msg);
        var tracker = this.annotationActivationTrackers.get(token);
        if (tracker && tracker.options) {
            // TODO: Expose annotation activation config to local workflows.
            // This is where the original config was stored, but there is no way to reach this from a local workflow yet.
            tracker.options.config = config;
        }
    };
    SessionProxy.prototype.releaseAnnotation = function (token) {
        var tracker = this.annotationActivationTrackers.get(token);
        if (tracker) {
            this.annotationActivationTrackers.delete(token);
            var map = this.annotationCallbacks.get(tracker.annotationType);
            if (map) {
                map.delete(token);
                if (map.size == 0) {
                    this.annotationCallbacks.delete(tracker.annotationType);
                }
            }
            return tracker.release();
        }
        return Promise.resolve(false);
    };
    SessionProxy.prototype.setAnnotationState = function (parentPath, id, value) {
        var M_ = { state: value };
        this.submitOperation(new UpdateAnnotationMetaDataOperation({ parentPath: parentPath, items: [{ id: id }], M_: M_ }));
    };
    SessionProxy.prototype.setAnnotationMetadata = function (parentPath, id, value) {
        this.submitOperation(new UpdateAnnotationMetaDataOperation({ parentPath: parentPath, items: [{ id: id }], M_: value }));
    };
    SessionProxy.prototype.submitOperation = function (operation, cv) {
        this.submitOperations([operation], cv);
    };
    SessionProxy.prototype.submitOperations = function (operations, cv) {
        var _this = this;
        this.contextIdManager.applyContextIdOnOperations(operations);
        var serverOps = operations.filter(function (op) { return isServerOp(op, _this.serverInputTypes); });
        this.submitOperationsToServer(serverOps, cv);
        if (this.localWorkflowOrchestrationMode === 0 /* Internal */) {
            this.localWorkflowManager.runLocalWorkflows(operations, this, true);
        }
    };
    SessionProxy.prototype.submitSeedOperations = function (operations, cv) {
        if (!this.allowSeed) {
            throw new Error('Cannot submit seed operations more than once per session');
        }
        this.allowSeed = false;
        this.allowGroupSeed = false;
        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, true, cv);
        }
        else {
            this.getSessionManager()
                .sendMessage(new SyncMessage({
                cv: cv,
                seq: 0,
                ops: operations
            }));
        }
    };
    SessionProxy.prototype.submitSeedGroupOperations = function (operations, groupComplete, cv) {
        if (!this.allowGroupSeed) {
            throw new Error('Seed operations are not allowed for this session');
        }
        if (groupComplete) {
            this.allowGroupSeed = false;
        }
        this.allowSeed = false;
        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, !!groupComplete, cv);
        }
        else {
            this.seedGroupSize++;
            this.getSessionManager()
                .sendMessage(new SyncMessage({
                cv: cv,
                seq: 0,
                ops: operations,
                groupId: 'Seed',
                groupSize: groupComplete ? this.seedGroupSize : undefined,
                groupComplete: groupComplete ? groupComplete : undefined
            }));
        }
    };
    SessionProxy.prototype.submitCustomMessage = function (message) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.getSessionManager()
                .sendMessage(message, function (err, response) {
                if (err) {
                    reject(new Error(err.error));
                }
                else {
                    resolve(response);
                }
            });
        });
    };
    SessionProxy.prototype.requestCacheDump = function (cacheRequestParameters) {
        var _this = this;
        if (cacheRequestParameters) {
            throw new Error('NYI');
        }
        else {
            var toReturn = new Promise(function (resolve, reject) {
                _this.getSessionManager()
                    .sendMessage(new CacheDumpRequestMessage(), (function (err, response) {
                    if (!err) {
                        resolve(response);
                    }
                    else {
                        reject(err);
                    }
                }));
            });
            return toReturn;
        }
    };
    SessionProxy.prototype.forceReconnect = function (extensionConfigs) {
        this.extensionConfigs = extensionConfigs;
        return this.getSessionManager().forceReconnect(extensionConfigs);
    };
    SessionProxy.prototype.close = function () {
        this.isSessionClosed = true;
        this.getSessionManager().closeSession();
        this.localWorkflowManager.closeSession(this);
    };
    SessionProxy.prototype.getClientMetadata = function () {
        return this.clientMetadata;
    };
    SessionProxy.prototype.setSessionCloseCallback = function (callback) {
        this.sessionCloseCallback = callback;
    };
    SessionProxy.prototype.setConnectCallback = function (callback) {
        this.connectCallback = callback;
    };
    SessionProxy.prototype.setDisconnectCallback = function (callback) {
        this.disconnectCallback = callback;
    };
    SessionProxy.prototype.setReconnectCallback = function (callback) {
        this.reconnectCallback = callback;
    };
    SessionProxy.prototype.onAnnotationResults = function (message, source, responseCallback) {
        var _this = this;
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new Response());
        // Notify annotation activation callbacks and apply operations if context candidates
        this.annotationResultsProcessor.process(message, function (operation, item) {
            _this.applyOperationForContext(operation, item, source);
        }, function (operation) {
            _this.callAnnotationCallbacks(operation, message.annotationType);
            if (_this.hostCallbacks.onAnnotationResult) {
                try {
                    _this.hostCallbacks.onAnnotationResult(operation);
                }
                catch (error) {
                    Log.error(0x2034575f /* tag_6nf35 */, LogCategory.WorkflowDefault, "onAnnotationResult error: " + error);
                }
            }
        }, function (message) {
            _this.contextIdManager.applyContextIdOnOperations(message.ops);
            if (source !== 1 /* ServerWorkflow */) {
                var serverOps = message.ops.filter(function (op) { return isServerOp(op, _this.serverInputTypes); });
                _this.submitOperationsToServer(serverOps, message.cv);
            }
            if (_this.localWorkflowOrchestrationMode === 0 /* Internal */) {
                _this.localWorkflowManager.runLocalWorkflows(message.ops, _this);
            }
        });
    };
    SessionProxy.prototype.onAnnotationResultStateMessage = function (message, responseCallback) {
        var e_2, _a, e_3, _b;
        var _c;
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new Response());
        try {
            for (var _d = __values(message.updates), _e = _d.next(); !_e.done; _e = _d.next()) {
                var update = _e.value;
                var annotationType = update.annotationType, state = update.state;
                if (!this.tokensByAnnotationType.has(annotationType)) {
                    continue;
                }
                // Get or determine old state if it's not available.
                // Updates are only sent if state has changed, so we can
                // determine the old state if it's not previously known.
                var oldState = void 0;
                if (this.annotationResultStates.has(annotationType)) {
                    oldState = this.annotationResultStates.get(annotationType);
                }
                else {
                    if (state === 0 /* Idle */) {
                        oldState = 1 /* Pending */;
                    }
                    else {
                        oldState = 0 /* Idle */;
                    }
                }
                try {
                    for (var _f = (e_3 = void 0, __values(this.tokensByAnnotationType.get(annotationType))), _g = _f.next(); !_g.done; _g = _f.next()) {
                        var token = _g.value;
                        var tracker = this.annotationActivationTrackers.get(token);
                        if ((_c = tracker.options) === null || _c === void 0 ? void 0 : _c.stateUpdateCallback) {
                            tracker.options.stateUpdateCallback(oldState, state);
                        }
                    }
                }
                catch (e_3_1) { e_3 = { error: e_3_1 }; }
                finally {
                    try {
                        if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
                    }
                    finally { if (e_3) throw e_3.error; }
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_2) throw e_2.error; }
        }
    };
    SessionProxy.prototype.onWorkflowExecutionRequest = function (message, responseCallback) {
        this.localWorkflowManager.onWorkflowExecutionRequest(this, message.workflowId, message.batchExecutionInputs[0], // Assume no batching for now
        function (err, result) {
            if (err) {
                responseCallback(undefined, new WorkflowExecutionResponse({ error: err.message }));
            }
            else {
                responseCallback(undefined, new WorkflowExecutionResponse({ batchExecutionResults: [result] }));
            }
        });
    };
    SessionProxy.prototype.submitOperationsToServer = function (serverOps, cv) {
        var _this = this;
        var isSignalOp = function (op) { return SchemaObject.getTypeNameFor(op) === SignalOperation.getTypeName(); };
        var signalServerOps = serverOps.filter(function (op) { return isSignalOp(op); });
        var modelServerOps = serverOps.filter(function (op) { return !isSignalOp(op); });
        // Signal ops are sent immediately regardless of batch config and are not sequenced
        if (signalServerOps.length > 0) {
            this.getSessionManager().sendMessage(new SyncMessage({
                cv: cv,
                ops: signalServerOps
            }));
        }
        if (modelServerOps.length > 0) {
            if (this.operationBatchConfig) {
                if (!this.batchedOperationsManager) {
                    var submit = function (operations, batch, callback) {
                        _this.getSessionManager().sendMessage(new SyncMessage({
                            cv: batch.cv,
                            seq: _this.nextSyncSequenceId++,
                            ops: operations
                        }), function (errorResponse) {
                            callback(errorResponse ? new Error(errorResponse.error) : undefined);
                        });
                    };
                    this.batchedOperationsManager = new BatchManager(submit);
                }
                this.batchedOperationsManager.addBatchItem(modelServerOps, this.operationBatchConfig, { name: 'SubmitOperations' }, cv);
            }
            else {
                this.getSessionManager().sendMessage(new SyncMessage({
                    cv: cv,
                    seq: this.nextSyncSequenceId++,
                    ops: modelServerOps
                }));
            }
        }
    };
    SessionProxy.prototype.registerContextTypes = function (allContextTypes) {
        var e_4, _a;
        try {
            for (var allContextTypes_1 = __values(allContextTypes), allContextTypes_1_1 = allContextTypes_1.next(); !allContextTypes_1_1.done; allContextTypes_1_1 = allContextTypes_1.next()) {
                var contextType = allContextTypes_1_1.value;
                // Register all context types of this workflow (both required and optional) and activate all their annotations on server side, so the server can return context annotations if they exist or get produced.
                this.activateAnnotation(contextType);
                this.registeredContextTypes.add(contextType);
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (allContextTypes_1_1 && !allContextTypes_1_1.done && (_a = allContextTypes_1.return)) _a.call(allContextTypes_1);
            }
            finally { if (e_4) throw e_4.error; }
        }
    };
    SessionProxy.prototype.applyOperationForContext = function (operation, item, source) {
        var _this = this;
        var operationType = SchemaObject.getTypeNameFor(operation);
        if (operationType != AddOperation.getTypeName() && operationType != Operation.getTypeName() && operationType != UpdateOperation.getTypeName() && operationType != DeleteOperation.getTypeName()) {
            return;
        }
        // Assume that generic operation is always the same as Add.
        if (operationType == Operation.getTypeName()) {
            operationType = AddOperation.getTypeName();
        }
        // TODO #5326116: Currently, context annotation can be uploaded or returned from server on any path. This is fine for now
        // as local session is bound to a single user and a single document. We need proper session cache in local runtime to check
        // exact context holder paths.
        var itemWithPath = shallowCloneItemWithPath(this.resolvePlaceholdersInOperationParentPath(operation.parentPath), item);
        var parentPathKey = makeItemPathKey(itemWithPath.parentPath);
        if (operationType == AddOperation.getTypeName() || operationType == UpdateOperation.getTypeName()) {
            if (!itemWithPath.body) {
                return;
            }
            var contextType = SchemaObject.getTypeNameFor(itemWithPath.body);
            if (!this.registeredContextTypes.has(contextType)) {
                return;
            }
            var contextsOfType = this.availableContexts.get(contextType);
            if (!contextsOfType) {
                contextsOfType = new Map();
                this.availableContexts.set(contextType, contextsOfType);
            }
            var contextsForSource = contextsOfType.get(source);
            if (!contextsForSource) {
                contextsForSource = [];
                contextsOfType.set(source, contextsForSource);
            }
            if (!itemWithPath.id) {
                itemWithPath.id = this.localWorkflowManager.getNextClientAnnotationId();
            }
            if (operationType == AddOperation.getTypeName()) {
                if (contextsForSource.filter(function (context) { return makeItemPathKey(context.parentPath) == parentPathKey && context.id == itemWithPath.id; }).length == 0) {
                    contextsForSource.push(itemWithPath);
                }
                else {
                    Log.info(0x203cb30f /* tag_6plmp */, LogCategory.WorkflowDefault, "AddOperation for (" + source + ", " + itemWithPath.id + ", " + itemWithPath.source + ") can't be applied as the context item with matching path and ID already exists. Use UpdateOperation instead.");
                }
            }
            else {
                for (var i = 0; i < contextsForSource.length; i++) {
                    if (contextsForSource[i].id == itemWithPath.id && makeItemPathKey(contextsForSource[i].parentPath) == parentPathKey) {
                        if (contextsForSource[i].source == itemWithPath.source) {
                            contextsForSource[i] = itemWithPath;
                            break;
                        }
                        else {
                            Log.warn(0x203cb310 /* tag_6plmq */, LogCategory.WorkflowDefault, "UpdateOperation for (" + source + ", " + itemWithPath.id + ", " + itemWithPath.source + ") can't be applied as the context item with provided path and ID has different source: " + contextsForSource[i].source);
                        }
                    }
                    else {
                        Log.warn(0x203cb311 /* tag_6plmr */, LogCategory.WorkflowDefault, "UpdateOperation for (" + source + ", " + itemWithPath.id + ", " + itemWithPath.source + ") can't be applied as the context item with matching path and ID was not found. AddOperation should be used instead");
                    }
                }
            }
        }
        else if (operationType == DeleteOperation.getTypeName()) {
            if (!itemWithPath.id) {
                return;
            }
            this.availableContexts.forEach(function (contextsOfType, contextType) {
                var contextsForSource = contextsOfType.get(source);
                if (!contextsForSource) {
                    return;
                }
                contextsForSource = contextsForSource.filter(function (context) { return !(makeItemPathKey(context.parentPath) == parentPathKey && context.id == itemWithPath.id); });
                if (contextsForSource.length == 0) {
                    contextsOfType.delete(source);
                }
                else {
                    contextsOfType.set(source, contextsForSource);
                }
                if (contextsOfType.size == 0) {
                    _this.availableContexts.delete(contextType);
                }
                else {
                    _this.availableContexts.set(contextType, contextsOfType);
                }
            });
        }
    };
    SessionProxy.prototype.resolveRequestedContexts = function (workflow) {
        var e_5, _a;
        var requestedContexts = [];
        try {
            for (var _b = __values(getContextTypes(workflow.requestedContextTypesRules)), _c = _b.next(); !_c.done; _c = _b.next()) {
                var _d = __read(_c.value, 2), contextType = _d[0], cardinality = _d[1];
                if (!this.availableContexts.has(contextType)) {
                    if (cardinality == -3 /* Required */) {
                        return [false, []];
                    }
                    else {
                        continue;
                    }
                }
                var allContextsOfType = Array.from(this.availableContexts.get(contextType).values()).reduce(function (acc, val) { return acc.concat(val); }, []);
                if (allContextsOfType.length == 0) {
                    throw new Error("Assert: availableContexts have the entry for " + contextType + " which does not contain any context annotation.");
                }
                requestedContexts.push.apply(requestedContexts, __spreadArray([], __read(allContextsOfType), false));
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_5) throw e_5.error; }
        }
        return [true, requestedContexts];
    };
    SessionProxy.prototype.getContextAnnotations = function (contextType, sourcePrefix, parentPath, sourceName) {
        var _a, _b;
        var parentPathKey = parentPath ? makeItemPathKey(parentPath) : undefined;
        return (_b = (_a = this.availableContexts.get(contextType)) === null || _a === void 0 ? void 0 : _a.get(sourcePrefix)) === null || _b === void 0 ? void 0 : _b.filter(function (context) { return (!parentPathKey || makeItemPathKey(context.parentPath) == parentPathKey) && (!sourceName || context.source == sourceName); });
    };
    SessionProxy.prototype.onWorkflowDefinitionOverrideMessage = function (message) {
        var logOp = new LogOperation({ operationName: 'WorkflowDefinitionOverride', success: true });
        var handleError = function (message) {
            logOp.resultDescription = message;
            logOp.success = false;
            Log.error(0x1f70969f /* tag_52j05 */, LogCategory.CoreDefault, logOp.stop());
            throw Error("WorkflowDefinitionOverride: " + message);
        };
        var workflowDefinitionsByName = this.localWorkflowManager.getWorkflowDefinitionsByName(this);
        var sourceWorkflow = workflowDefinitionsByName.get(message.sourceWorkflowId);
        if (!sourceWorkflow) {
            handleError("Workflow registration for source workflow '" + message.targetWorkflowId + "' was not found.");
            return;
        }
        var targetWorkflow = workflowDefinitionsByName.get(message.targetWorkflowId);
        if (!targetWorkflow) {
            handleError("Workflow registration for target workflow '" + message.targetWorkflowId + "' was not found.");
            return;
        }
        if (!targetWorkflow.allowDefinitionOverride) {
            handleError("Workflow '" + targetWorkflow.id + "' does not allow workflow definition override.");
            return;
        }
        if (sourceWorkflow.definitionOverrideTargetWorkflows.indexOf(targetWorkflow.id) === -1) {
            handleError("Workflow '" + sourceWorkflow.id + "' does not allow workflow definition for workflow '" + targetWorkflow.id + "'.");
            return;
        }
        this.workflowDefinitionManager.mergeWorkflowDefinition(targetWorkflow, message.definition, message.contextId);
        logOp.success = true;
        logOp.resourceId = sourceWorkflow.id;
        logOp.resultDescription = "Updated config for workflow: " + message.targetWorkflowId + ", context id: " + message.contextId;
        Log.info(0x1f7096a0 /* tag_52j06 */, LogCategory.CoreDefault, logOp.stop());
    };
    SessionProxy.prototype.applyContextIdOnOperations = function (ops) {
        this.contextIdManager.applyContextIdOnOperations(ops);
    };
    SessionProxy.prototype.getAuthToken = function (tokenType, cb) {
        var tokenRequest = { Tickets: [] };
        if (this.clientMetadata.docSessionId) {
            tokenRequest.DocSessionId = this.clientMetadata.docSessionId;
        }
        var clientTokenType = getClientAuthTokenType(tokenType);
        if (clientTokenType) {
            tokenRequest.TokenType = clientTokenType;
        }
        this.hostCallbacks.requestAuthToken(tokenRequest)
            .then(function (tokenResponse) {
            if (!tokenResponse) {
                cb(new Error('Missing AuthTokenResponse from requestAutToken'));
                return;
            }
            cb(undefined, tokenResponse.Token);
            return;
        })
            .catch(function (error) {
            return cb(error);
        });
    };
    SessionProxy.prototype.resetNextSyncSequenceId = function () {
        this.nextSyncSequenceId = 1;
    };
    SessionProxy.prototype.onReconnect = function () {
        this.isSessionConnected = true;
        this.annotationActivationTrackers.forEach(function (tracker) {
            tracker.activate(true /*ignoreExistingAnnotations*/);
        });
        if (this.reconnectCallback) {
            this.reconnectCallback();
        }
    };
    SessionProxy.prototype.onSessionClose = function (sessionCloseMessage) {
        this.isSessionClosed = true;
        this.isSessionConnected = false;
        if (this.sessionCloseCallback) {
            this.sessionCloseCallback(sessionCloseMessage);
        }
    };
    SessionProxy.prototype.onConnect = function (isSeedingRequired, sessionUrl, origin, authToken, serverInputTypes) {
        if (this.hasSessionConnected) {
            // If seeding is required on reconnect reset allowSeed flags, seedGroupSize counter, and seedBatchedOperationsManager.
            // Otherwise we will continue with already started seeding if reconnect occurred during the seeding
            if (isSeedingRequired) {
                this.allowSeed = true;
                this.allowGroupSeed = true;
                this.seedGroupSize = 0;
                if (this.seedBatchedOperationsManager) {
                    this.seedBatchedOperationsManager.removeAllBatchedItems();
                    this.seedBatchedOperationsManager = undefined;
                }
                if (this.batchedOperationsManager) {
                    this.batchedOperationsManager.removeAllBatchedItems();
                    this.batchedOperationsManager = undefined;
                }
            }
        }
        this.isSessionConnected = true;
        this.hasSessionConnected = true;
        if (this.connectCallback) {
            this.connectCallback(isSeedingRequired, sessionUrl, origin, authToken);
        }
        this.serverInputTypes = serverInputTypes;
    };
    SessionProxy.prototype.onDisconnect = function (error) {
        this.isSessionConnected = false;
        if (this.disconnectCallback) {
            this.disconnectCallback(error);
        }
    };
    SessionProxy.prototype.getSessionManager = function () {
        if (!this.sessionManager) {
            this.sessionManager = new EmptySessionManager();
        }
        return this.sessionManager;
    };
    SessionProxy.prototype.callAnnotationCallbacks = function (op, annotationType) {
        if (this.annotationCallbacks.size === 0) {
            return;
        }
        var callbackMap = this.annotationCallbacks.get(annotationType);
        if (callbackMap) {
            callbackMap.forEach(function (callback) {
                callback(op);
            });
        }
    };
    SessionProxy.prototype.getOperationBatchConfig = function (batchOptions) {
        // Group all arrays of operations together
        var groupingKeyExtractor = function () {
            return 'operations';
        };
        // Combine operations into a single array
        var multiplex = function (operations) {
            return {
                input: operations.filter(function (ops) { return ops.length; }).reduce(function (ops, array) { return ops.concat(array); }, []),
                demultiplex: function () { return [[]]; } // NOTE: Unused because no callback is specified to addBatchItem
            };
        };
        // Split large operations arrays into smaller arrays
        var split = function (operations) {
            // Cannot split array with 1 or fewer operations
            if (operations.length <= 1) {
                return undefined;
            }
            return {
                inputs: operations.reduce(function (ops, op) {
                    ops.push([op]);
                    return ops;
                }, []),
                join: function () { return []; } // NOTE: Unused because no callback is specified to addBatchItem
            };
        };
        return {
            delayMs: batchOptions.delayMs,
            maxInputSize: batchOptions.maxInputSize,
            estimateSize: estimateSize,
            groupingKeyExtractor: groupingKeyExtractor,
            multiplex: multiplex,
            split: split
        };
    };
    SessionProxy.prototype.sendSeedMessagesViaBatchManager = function (operations, groupComplete, cv) {
        var _this = this;
        if (!this.seedBatchedOperationsManager) {
            // Submit will be invoked once per batch of operations
            var submit = function (operations, batch, callback) {
                // Update the count of batches
                _this.seedGroupSize++;
                // Send the SyncMessage for the operations in this batch
                _this.getSessionManager().sendMessage(new SyncMessage({
                    cv: batch.cv,
                    seq: 0,
                    ops: operations,
                    groupId: 'Seed',
                    groupSize: batch.groupComplete ? _this.seedGroupSize : undefined,
                    groupComplete: batch.groupComplete ? batch.groupComplete : undefined
                }), function (errorResponse) {
                    callback(errorResponse ? new Error(errorResponse.error) : undefined);
                });
            };
            // Instantiate the seed batch manager
            this.seedBatchedOperationsManager = new BatchManager(submit);
        }
        // Instantiate the seed batch manager and add the seed operations to it
        this.seedBatchedOperationsManager.addBatchItem(operations, this.operationBatchConfig, { name: 'SubmitSeedOperations' }, cv, groupComplete);
    };
    SessionProxy.prototype.onAnnotationResultsFromServer = function (message, responseCallback) {
        this.onAnnotationResults(message, 1 /* ServerWorkflow */, responseCallback);
    };
    SessionProxy.prototype.resolvePlaceholdersInOperationParentPath = function (parentPath) {
        // AL server replaces #userContext# and #tenantContext# placeholders with actual paths of context holders for the current user and tenant. However, local session is not aware of userID and tenantID as it's bound to a single user.
        // So, if local runtime received context with already resolved user / tenant context holder path, replace it back with placeholder, so we don't need to track user / tenant contexts under the two parents.
        var localParentPath = parentPath;
        if (localParentPath.length == 3 && localParentPath[0] == 'session' && localParentPath[1] == 'user' && localParentPath[2].startsWith('user_')) {
            Log.info(0x203cb312 /* tag_6plms */, LogCategory.WorkflowDefault, "Replacing user context placeholder for: " + makeItemPathKey(parentPath) + ".");
            localParentPath = ['session', '#userContext#'];
        }
        if (localParentPath.length == 3 && localParentPath[0] == 'session' && localParentPath[1] == 'user' && localParentPath[2].startsWith('tenant_')) {
            Log.info(0x203cb313 /* tag_6plmt */, LogCategory.WorkflowDefault, "Replacing tenant context placeholder for: " + makeItemPathKey(parentPath) + ".");
            localParentPath = ['session', '#tenantContext#'];
        }
        return localParentPath;
    };
    return SessionProxy;
}());
export { SessionProxy };
//# sourceMappingURL=session-proxy.js.map